/**
 * Ask for a block, or a whole phase, again.
 *
 * The coach addresses the node they are looking at — a block in a session, or
 * the phase column itself — and the button is the action: one click asks for
 * that node again.
 *
 * The arc and the target are read off the page rather than typed: the page
 * already knows which phase a block belongs to and where it sits, which is the
 * whole difference between this and calling the pipeline by hand.
 *
 * The run is the committed `regenerate-block` pipeline, unmodified — the same
 * graph the MCP tool runs. Nothing here forks it.
 */

import { useCallback, useState } from "react";
import { runOnNexus } from "../../felix";
import type { Phase } from "./types";

export interface RegenTarget {
  phase: number;
  workout?: string;
  block?: number;
}

export interface RegenResult {
  arc?: Phase[];
  changed?: boolean;
  refused?: boolean;
  scope?: "block" | "phase";
  note?: string;
  target?: RegenTarget;
  violations?: { check: string; message: string }[];
  repairs?: unknown[];
}

/** What the coach is asking about, in the words the page uses for it. */
export function targetLabel(t: RegenTarget, phaseLabel: string): string {
  return t.block
    ? `${phaseLabel} · ${t.workout || "this session"} · Block ${t.block}`
    : phaseLabel;
}

async function pipelineGraph(instance: string): Promise<any> {
  const r = await fetch(`/api/${instance}/pipelines`);
  if (!r.ok) throw new Error(`pipelines ${r.status}`);
  const d = await r.json() as { pipelines?: { id?: string; graph?: any }[] };
  const found = (d.pipelines || []).find(p => p.id === "regenerate-block");
  const graph = found?.graph ?? found;
  if (!graph?.nodes) throw new Error("the regenerate-block pipeline is not published");
  return graph;
}

/** Run one regeneration. Returns the designer's result, refusal included — a
 *  refused reroll is an answer, not a failure, and the caller shows why. */
export function useRegenerate(instance: string, student: string) {
  const [busy, setBusy] = useState<RegenTarget | null>(null);
  const [error, setError] = useState<string>("");
  const [result, setResult] = useState<RegenResult | null>(null);

  const run = useCallback(async (arc: Phase[], target: RegenTarget, note: string) => {
    setBusy(target);
    setError("");
    setResult(null);
    try {
      const g = JSON.parse(JSON.stringify(await pipelineGraph(instance)));
      g.instance = `instances/${instance}`;
      for (const n of g.nodes || []) {
        if (n.kind === "input") {
          n.config = { ...(n.config || {}), files: [], student, arc, target, note };
        }
      }
      // Held in a box: the assignment happens inside the run's callback, which
      // control-flow analysis cannot see.
      const box: { value: RegenResult | null } = { value: null };
      await runOnNexus(instance, g, {
        onNodeDone: (_id, r: any) => {
          const rows = r?.output?.rows ?? r?.rows;
          if (Array.isArray(rows) && rows[0]?.arc) box.value = rows[0] as RegenResult;
        },
      });
      if (!box.value) throw new Error("the run finished without returning an arc");
      setResult(box.value);
      return box.value;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(null);
    }
  }, [instance, student]);

  return { run, busy, error, result, clear: () => { setResult(null); setError(""); } };
}

/** The outcome, stated where the coach asked. A refusal names the rule that
 *  refused it — the design they had is still the design they have. */
export function RegenerateOutcome({ result, onDismiss }: {
  result: RegenResult;
  onDismiss: () => void;
}) {
  const refused = Boolean(result.refused);
  return (
    <div className={`pr-regen-outcome ${refused ? "is-refused" : "is-done"}`}>
      <div className="pr-regen-outcome-head">
        {refused ? "Kept the current design" : "Regenerated"}
        <button className="pr-btn-quiet" onClick={onDismiss} type="button">Dismiss</button>
      </div>
      {refused ? (
        <ul className="pr-regen-violations">
          {(result.violations || []).map((v, i) => (
            <li key={`${v.check}-${i}`}>{v.message}</li>
          ))}
        </ul>
      ) : (
        <p className="pr-muted">
          {result.scope === "phase"
            ? "Every block in this phase was replaced. The rest of the arc is unchanged and the whole arc was re-validated."
            : "This block was replaced. Every other pick is unchanged and the whole arc was re-validated."}
        </p>
      )}
    </div>
  );
}

/** The ask itself. Quiet until hovered, and it states what it is doing while it
 *  runs — a design takes minutes, so a button that only greys out reads as a
 *  page that has stopped responding. */
export function RegenerateButton({ label, busy, onClick }: {
  label: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="pr-regen-btn"
      disabled={busy}
      onClick={onClick}
      title={busy ? "Designing…" : `Ask for ${label} again`}
      type="button"
    >
      {busy ? "Designing…" : "Regenerate"}
    </button>
  );
}
